import React from 'react';
import {
  CLASSIFICATION_OPTIONS,
  EXPORT_LANGUAGE_MODES,
  LANGUAGE_OPTIONS,
  LOCALE_OPTIONS,
  REGISTER_OPTIONS,
} from '../constants';
import { tUi } from '../utils/uiText';

export default function EngagementBar({
  uiLanguage = 'en',
  clientName,
  projectName,
  reportTemplateId,
  reportTemplates,
  classification,
  customClassification,
  chatLanguage,
  reportLanguage,
  analystLanguage,
  customChatLanguage,
  customReportLanguage,
  customAnalystLanguage,
  locale,
  languageRegister,
  exportLanguageMode,
  onClientNameChange,
  onProjectNameChange,
  onReportTemplateChange,
  onClassificationChange,
  onCustomClassificationChange,
  onChatLanguageChange,
  onReportLanguageChange,
  onAnalystLanguageChange,
  onCustomChatLanguageChange,
  onCustomReportLanguageChange,
  onCustomAnalystLanguageChange,
  onLocaleChange,
  onLanguageRegisterChange,
  onExportLanguageModeChange,
  onTranslateChat,
  translatingChat,
  onSave,
  saving,
}) {
  const renderLanguageSelect = (id, label, value, onChange, customValue, onCustomChange) => (
    <div className="min-w-[180px] flex-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
      >
        {LANGUAGE_OPTIONS.map((language) => (
          <option key={language.code} value={language.code}>{language.label}</option>
        ))}
        <option value="__custom__">{tUi(uiLanguage, 'engagementOtherLanguage')}</option>
      </select>
      {value === '__custom__' && (
        <input
          value={customValue}
          onChange={(e) => onCustomChange(e.target.value)}
          placeholder={tUi(uiLanguage, 'engagementCustomLanguagePlaceholder')}
          className="mt-2 w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
        />
      )}
    </div>
  );

  return (
    <div className="mb-4 bg-gray-900 rounded-lg border border-teal-500/30 p-4 space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[180px] flex-1">
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{tUi(uiLanguage, 'engagementClient')}</label>
          <input
            value={clientName}
            onChange={(e) => onClientNameChange(e.target.value)}
            placeholder={tUi(uiLanguage, 'engagementClientPlaceholder')}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{tUi(uiLanguage, 'engagementProject')}</label>
          <input
            value={projectName}
            onChange={(e) => onProjectNameChange(e.target.value)}
            placeholder={tUi(uiLanguage, 'engagementProjectPlaceholder')}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          />
        </div>
        <div className="min-w-[160px] flex-1">
          <label htmlFor="classification" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            {tUi(uiLanguage, 'engagementClassification')}
          </label>
          <select
            id="classification"
            value={classification}
            onChange={(e) => onClassificationChange(e.target.value)}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          >
            {CLASSIFICATION_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          {classification === '__custom__' && (
            <input
              value={customClassification}
              onChange={(e) => onCustomClassificationChange(e.target.value)}
              placeholder={tUi(uiLanguage, 'engagementCustomClassification')}
              className="mt-2 w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
            />
          )}
        </div>
        <div className="min-w-[220px] flex-1">
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{tUi(uiLanguage, 'engagementReportTemplate')}</label>
          <select
            value={reportTemplateId}
            onChange={(e) => onReportTemplateChange(e.target.value)}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          >
            {reportTemplates.map((template) => (
              <option key={template.id} value={template.id}>{template.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {renderLanguageSelect(
          'chat-language',
          tUi(uiLanguage, 'engagementYourLanguage'),
          chatLanguage,
          onChatLanguageChange,
          customChatLanguage,
          onCustomChatLanguageChange,
        )}
        {renderLanguageSelect(
          'report-language',
          tUi(uiLanguage, 'engagementClientLanguage'),
          reportLanguage,
          onReportLanguageChange,
          customReportLanguage,
          onCustomReportLanguageChange,
        )}
        {renderLanguageSelect(
          'analyst-language',
          tUi(uiLanguage, 'engagementAnalystLanguage'),
          analystLanguage,
          onAnalystLanguageChange,
          customAnalystLanguage,
          onCustomAnalystLanguageChange,
        )}
        <div className="min-w-[180px] flex-1">
          <label htmlFor="locale" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{tUi(uiLanguage, 'engagementLocale')}</label>
          <select
            id="locale"
            value={locale}
            onChange={(e) => onLocaleChange(e.target.value)}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          >
            {LOCALE_OPTIONS.map((item) => (
              <option key={item.code} value={item.code}>{item.label}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[180px] flex-1">
          <label htmlFor="language-register" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{tUi(uiLanguage, 'engagementRegister')}</label>
          <select
            id="language-register"
            value={languageRegister}
            onChange={(e) => onLanguageRegisterChange(e.target.value)}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          >
            {REGISTER_OPTIONS.map((item) => (
              <option key={item.code} value={item.code}>{item.label}</option>
            ))}
          </select>
        </div>
        <div className="min-w-[220px] flex-1">
          <label htmlFor="export-language-mode" className="block text-xs uppercase tracking-wide text-gray-400 mb-1">{tUi(uiLanguage, 'engagementExportMode')}</label>
          <select
            id="export-language-mode"
            value={exportLanguageMode}
            onChange={(e) => onExportLanguageModeChange(e.target.value)}
            className="w-full px-3 py-2 bg-black border border-teal-500/30 rounded-lg text-white"
          >
            {EXPORT_LANGUAGE_MODES.map((item) => (
              <option key={item.code} value={item.code}>{item.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <button
          type="button"
          onClick={onTranslateChat}
          disabled={translatingChat}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-700 border border-teal-500/30 rounded-lg text-white"
        >
          {translatingChat ? tUi(uiLanguage, 'engagementTranslatingChat') : tUi(uiLanguage, 'engagementTranslateChat')}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-700 text-black font-semibold rounded-lg"
        >
          {saving ? tUi(uiLanguage, 'commonSaving') : tUi(uiLanguage, 'engagementSave')}
        </button>
      </div>
      <p className="text-xs text-gray-500">
        {tUi(uiLanguage, 'engagementHint')}
      </p>
    </div>
  );
}
